// frontend/src/components/client/ClientInvoices.jsx
import React, { useState, useEffect } from 'react';
import { FileText, Download, Eye, CreditCard, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { getInvoices, getInvoiceStats } from '../../services/clientApi';

const StatusBadge = ({ status }) => {
  const config = {
    pending: { label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
    paid: { label: 'Paid', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
    overdue: { label: 'Overdue', color: 'bg-red-500/20 text-red-400', icon: AlertCircle }
  };
  const { label, color, icon: Icon } = config[status] || config.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${color}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

const ClientInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => { loadData(); }, [filter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [invoicesRes, statsRes] = await Promise.all([
        getInvoices(filter === 'all' ? null : filter),
        getInvoiceStats()
      ]);
      setInvoices(invoicesRes.data.invoices || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString() : 'N/A';

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

  return (
    <div className="px-4 sm:px-0">
      <div className="mb-5">
        <h2 className="text-xl font-bold text-white">Invoices</h2>
        <p className="text-gray-400 text-sm">View and manage your billing history</p>
      </div>

      {/* Stats Cards — 2 cols on mobile, 3 on sm+ */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
          {[
            { title: 'Pending', value: formatCurrency(stats.pending?.total), color: 'text-yellow-400' },
            { title: 'Paid', value: formatCurrency(stats.paid?.total), color: 'text-green-400' },
            { title: 'Total', value: (stats.pending?.count || 0) + (stats.paid?.count || 0), color: 'text-blue-400', span: 'col-span-2 sm:col-span-1' }
          ].map(({ title, value, color, span = '' }) => (
            <div key={title} className={`bg-gray-800/50 rounded-xl border border-gray-700 p-3 sm:p-4 ${span}`}>
              <p className="text-gray-400 text-xs sm:text-sm">{title}</p>
              <p className={`text-xl sm:text-2xl font-bold ${color} mt-1 truncate`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-4">
        {['all', 'pending', 'paid'].map((tab) => (
          <button
            key={tab}
            onClick={() => setFilter(tab)}
            className={`flex-1 sm:flex-none px-3 py-2 rounded-lg text-sm transition font-medium ${
              filter === tab
                ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                : 'bg-gray-700 text-gray-400 hover:bg-gray-600 active:bg-gray-500'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Invoices List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : invoices.length === 0 ? (
        <div className="text-center py-12 bg-gray-800/50 rounded-xl border border-gray-700">
          <FileText className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500">No invoices found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {invoices.map((inv) => (
            <div key={inv.id} className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg flex-shrink-0">
                  <FileText className="w-5 h-5 text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="text-white font-medium">#{inv.invoice_number}</span>
                    <StatusBadge status={inv.status} />
                  </div>
                  <p className="text-sm text-gray-400">{formatDate(inv.created_at)}</p>
                  <p className="text-lg font-bold text-white mt-0.5">{formatCurrency(inv.amount)}</p>
                  {inv.due_date && (
                    <p className="text-xs text-gray-500 mt-0.5">Due: {formatDate(inv.due_date)}</p>
                  )}
                </div>
              </div>
              {/* Actions row below on mobile */}
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-700/50">
                <button className="flex-1 sm:flex-none p-2 hover:bg-gray-700 active:bg-gray-600 rounded-lg text-gray-400 transition flex items-center justify-center gap-1">
                  <Eye className="w-4 h-4" />
                  <span className="text-xs sm:hidden">View</span>
                </button>
                <button className="flex-1 sm:flex-none p-2 hover:bg-gray-700 active:bg-gray-600 rounded-lg text-gray-400 transition flex items-center justify-center gap-1">
                  <Download className="w-4 h-4" />
                  <span className="text-xs sm:hidden">Download</span>
                </button>
                {inv.status === 'pending' && (
                  <button className="flex-1 sm:flex-none px-3 py-2 bg-green-500 hover:bg-green-600 active:bg-green-700 rounded-lg text-white text-sm flex items-center justify-center gap-1.5 font-medium transition">
                    <CreditCard className="w-4 h-4" />
                    Pay Now
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientInvoices;